import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { IMAGES } from '../constants/images';
import { OrderButton } from './OrderButton';
import { AmbientLight } from './AmbientLight';
import { ScrollBacklight } from './ScrollBacklight';
import { ORDER_URL } from '../constants/translations';
import { useLanguage } from '../context/LanguageContext';
export function Flavors() {
  const {
    t
  } = useLanguage();
  const list = t.flavors.list as Array<{
    name: string;
    copy: string;
    img?: keyof typeof IMAGES;
  }>;
  const withPhoto = list.filter(f => f.img);
  const textOnly = list.filter(f => !f.img);
  return <section id="flavors" className="relative overflow-hidden py-32 lg:py-48 bg-texture-dark text-cream" data-id="element-93" data-mp="3ixns5u3ofnp8">
      <AmbientLight variant="corner" seam data-id="element-94" data-mp="2qbcrykjzfosy" />
      <div className="relative z-10 max-w-[1300px] mx-auto px-8 lg:px-14" data-id="element-95" data-mp="2qabhah6vp4br">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 1
      }} className="max-w-3xl" data-id="element-96" data-mp="2v9ebt7p8fs5h">
          <div className="flex items-center gap-3 text-caramel text-[10px] tracking-[0.4em] uppercase mb-10" data-id="element-97" data-mp="coz06w9lkhoj">
            <span className="w-10 h-px bg-caramel/40" data-id="element-98" data-mp="7pmiepxl20st" />
            {t.flavors.eyebrow}
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-cream leading-[0.95] tracking-[-0.02em] font-light" data-id="element-99" data-mp="2q9ue1uz6non">
            {t.flavors.title1}
            <br data-id="element-100" data-mp="3uwogqrpkrtcg" />
            <span className="italic font-extralight" data-id="element-101" data-mp="3pvh8ysnfmu5s">{t.flavors.title2}</span>
          </h2>

          <div className="mt-10 flex" data-id="element-102" data-mp="3pxu281yn4qy5">
            <OrderButton data-id="element-103" data-mp="1t3h5pugzvxwd" />
          </div>
        </motion.div>

        {/* Flavors with photography — framed open-box cards (click to order) */}
        <div className="mt-20 lg:mt-28 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10" data-id="element-104" data-mp="1o21ibvrchfat">
          {withPhoto.map((f, i) => <motion.a key={f.name} href={ORDER_URL} target="_blank" rel="noreferrer" aria-label={`${t.order.label} — ${f.name}`} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.9,
          delay: i * 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="group block focus:outline-none focus-visible:ring-1 focus-visible:ring-caramel focus-visible:ring-offset-4 focus-visible:ring-offset-chocolate" data-id="element-105" data-mp="2bz7kyvs6g5cs" data-mpi={i}>
              <div className="relative" data-id="element-106" data-mp="1aji4zwvp2omq">
                <ScrollBacklight tone="caramel" data-id="element-107" data-mp="1agduyivjsyo5" />
                <div className="absolute -inset-2 border border-cream/10 transition-colors duration-500 group-hover:border-caramel/40" aria-hidden data-id="element-108" data-mp="12y66rmxrv2uj" />
                <div className="relative aspect-[4/5] overflow-hidden bg-espresso" data-id="element-109" data-mp="1bzvzqx7ay9uf">
                  <img src={IMAGES[f.img!]} alt={`TARTA ${f.name} cheesecake, shown open in its signature box`} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]" data-id="element-110" data-mp="17018utnj1k9d" />
                  {/* Order affordance — reveals on hover/focus */}
                  <div className="absolute inset-0 flex items-end justify-start p-5 bg-gradient-to-t from-espresso/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500" aria-hidden data-id="element-111" data-mp="t2us14md5vms">
                    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-cream" data-id="element-112" data-mp="984hdh7pu37n">
                      {t.order.label}
                      <ArrowUpRightIcon className="h-3.5 w-3.5 text-caramel" strokeWidth={1.75} data-id="element-113" data-mp="e7h5s6e8jicc" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-baseline justify-between gap-3" data-id="element-114" data-mp="rz46cif7nd0x">
                <h3 className="font-serif text-2xl lg:text-[28px] text-cream font-light tracking-[-0.01em] leading-tight transition-colors duration-500 group-hover:text-caramel" data-id="element-115" data-mp="wxfcoj6ar5ee">
                  {f.name}
                </h3>
                <span className="font-serif italic text-caramel text-sm shrink-0" data-id="element-116" data-mp="16w4te8mgijt2">
                  0{i + 1}
                </span>
              </div>
              <p className="mt-3 text-cream/55 leading-[1.7] text-[14px]" data-id="element-117" data-mp="33qht0ozefsz2">
                {f.copy}
              </p>
            </motion.a>)}
        </div>

        {/* Flavors without photography yet — refined text rows */}
        {textOnly.length > 0 && <div className="mt-20 lg:mt-24 border-t border-cream/10" data-id="element-118" data-mp="1kzu4yceje8v2">
            {textOnly.map((f, i) => <motion.a key={f.name} href={ORDER_URL} target="_blank" rel="noreferrer" aria-label={`${t.order.label} — ${f.name}`} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.7,
          delay: i * 0.06
        }} className="group grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-8 items-baseline py-8 lg:py-10 border-b border-cream/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-caramel focus-visible:ring-offset-4 focus-visible:ring-offset-chocolate" data-id="element-119" data-mp="1h01svppxpkky" data-mpi={i}>
                <div className="sm:col-span-1 flex items-baseline gap-3" data-id="element-120" data-mp="bsirmabcweii">
                  <span className="font-serif italic text-caramel text-sm" data-id="element-121" data-mp="bs8w677jlf5f">
                    0{withPhoto.length + i + 1}
                  </span>
                </div>
                <div className="sm:col-span-4" data-id="element-122" data-mp="21o12tr2usr9">
                  <h3 className="font-serif text-3xl lg:text-[40px] text-cream font-light tracking-[-0.01em] group-hover:italic group-hover:text-caramel transition-all duration-500" data-id="element-123" data-mp="20w9rv4ijh6s">
                    {f.name}
                  </h3>
                </div>
                <div className="sm:col-span-7" data-id="element-124" data-mp="3t9tlhzrpndkk">
                  <p className="text-cream/55 leading-[1.7] text-[15px] max-w-md" data-id="element-125" data-mp="3f98yssiocxls">
                    {f.copy}
                  </p>
                </div>
              </motion.a>)}
          </div>}

        <p className="mt-12 text-[11px] tracking-[0.2em] uppercase text-cream/40 max-w-lg leading-[1.7]" data-id="element-126" data-mp="30m0lnm5t2i76">
          {t.flavors.footnote}
        </p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8
      }} className="mt-16 flex justify-center" data-id="element-127" data-mp="2vpa4swm6fikp">
          <OrderButton withSub data-id="element-128" data-mp="3els4q1b6ios1" />
        </motion.div>
      </div>
    </section>;
}